import {HTTPError, HTTPCodesEnum} from '@jetbrains/teamcity-ui/dist/services'
import type {QueryArgFrom} from '@reduxjs/toolkit/query'
import * as React from 'react'
import {Fragment} from 'react'
import type {LoaderFunctionArgs, RouteObject} from 'react-router'
import {createBrowserRouter, redirect} from 'react-router'
import type {IEnvironment} from 'relay-runtime'

import {loadBuildsData} from '../../actions/builds'
import getEnvironment from '../../relay/getEnvironment'
import {getAgentPreviewsArg} from '../../rest/agents'
import {getChangesArg} from '../../rest/arguments'
import {getSingleBuildArg} from '../../rest/builds'
import {getBuildLocator, getLocator} from '../../rest/locators'
import {sidebarArg} from '../../rest/projects'
import {getTabsArg} from '../../rest/tabs'
import Routes, {getHref, isAgentsScreen, isProjectSidebarScreen} from '../../routes'
import type {EntryPointRoute} from '../../routes/entryPointRoute'
import {entryPointRoute} from '../../routes/entryPointRoute'
import type {
  AnyQueryDefinition,
  Endpoint,
  LoadedEndpoints,
  PreloadedEndpointsLoaderData,
} from '../../routes/loadEndpoint'
import {loadEndpoint} from '../../routes/loadEndpoint'
import {getExtensionEndpoint, getUserProperty} from '../../selectors'
import {restApi} from '../../services/rest'
import store from '../../store'
import {
  STARRED_LOCATOR,
  stringifyId,
  toAgentId,
  toAgentPoolId,
  toAgentTypeId,
  toBuildId,
  toBuildTypeId,
  toChangeId,
  toProjectId,
} from '../../types'
import {
  isInvestigationsPageEnabled,
  isPipelinesEnabledInExclusiveMode,
} from '../../utils/featureToggles'
import {getAvailabilityErrorMessage} from '../../utils/getAvailabilityErrorMessage'
import {notNull} from '../../utils/guards'
import {queryToObject} from '../../utils/queryParams'
import {getTabParamsKey} from '../../utils/tabs'
import {UserProperties} from '../../utils/userProperties'
import ErrorPage from '../common/ErrorPage/ErrorPage'
import {submitPager} from '../common/Pager/Pager.actions.base'
import {getTestArg} from '../packages/Tests/Tests.rest'
import {preloadAgentPage} from '../pages/AgentsPages/AgentPage/AgentPage.preload'
import {AgentPoolPageEntryPoint} from '../pages/AgentsPages/AgentPoolPage/AgentPoolPage.entryPoint'
import {AgentsPoolsFavoritePageEntryPoint} from '../pages/AgentsPages/AgentPoolsFavoritePage/AgentsPoolsFavoritePage.entryPoint'
import {preloadFavoriteAgentPoolsPage} from '../pages/AgentsPages/AgentPoolsFavoritePage/AgentsPoolsFavoritePage.preload'
import {AgentsOverviewPageEntryPoint} from '../pages/AgentsPages/AgentsOverviewPage/AgentsOverviewPage.entryPoint'
import {preloadAgentsOverviewPage} from '../pages/AgentsPages/AgentsOverviewPage/AgentsOverviewPage.preload'
import {AgentsPagesEntryPoint} from '../pages/AgentsPages/AgentsPages.entryPoint'
import {UnauthorizedAgentsPageEntryPoint} from '../pages/AgentsPages/UnauthorizedAgentsPage/UnauthorizedAgentsPage.entryPoint'
import {BuildPageEntryPoint} from '../pages/BuildPage/BuildPage.entryPoint'
import {preloadBuildPage} from '../pages/BuildPage/BuildPage.preload'
import BuildUnknownBuildTypePage from '../pages/BuildPage/BuildPageUnknownBuildType/BuildUnknownBuildTypePage'
import {BuildTypePageEntryPoint} from '../pages/BuildTypePage/BuildTypePage.entryPoint'
import {preloadBuildTypePage} from '../pages/BuildTypePage/BuildTypePage.preload'
import {CreateProjectPageEntryPoint} from '../pages/CreateProjectPage/CreateProjectPage.entryPoint'
import {FavoriteProjectsPageEntryPoint} from '../pages/FavoriteProjectsPage/FavoriteProjectsPage.entryPoint'
import {preloadFavoriteProjectsPage} from '../pages/FavoriteProjectsPage/FavoriteProjectsPage.preload'
import {NotificationsPageEntryPoint} from '../pages/NotificationsPage/NotificationsPage.entryPoint'
import {pipelinesPagesRoutes} from '../pages/PipelinesPages/PipelinesPages.routes'
import {ProjectPageEntryPoint} from '../pages/ProjectPage/ProjectPage.entryPoint'
import {preloadProjectPage} from '../pages/ProjectPage/ProjectPage.preload'
import {QueuePageEntryPoint} from '../pages/QueuePage/QueuePage.entryPoint'
import {getQueuePageLocator} from '../pages/QueuePage/QueuePage.utils'
import {TestHistoryPageEntryPoint} from '../pages/TestHistoryPage/TestHistoryPage.entryPoint'

import {AppAgentEntryPoint} from './Agent/AppAgent.entryPoint'
import {AppAgentOverviewEntryPoint} from './AgentOverview/AppAgentOverview.entryPoint'
import {preloadAppAgentOverview} from './AgentOverview/AppAgentOverview.preload'
import {AppAgentTypeEntryPoint} from './AgentType/AppAgentType.entryPoint'
import {preloadAppAgentType} from './AgentType/AppAgentType.preload'
import {AppEntryPoint} from './App.entryPoint'
import {getAvailabilityResponse} from './App.selectors'
import {preloadProjectsSidebar} from './SidebarPanel/SidebarPanelContent/ProjectsSidebar/ProjectsSidebar.preload'

const FavoriteBuildsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "FavoriteBuildsPage" */
      '../pages/FavoriteBuildsPage/FavoriteBuildsPage'
    ),
)
const CompareBuildsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CompareBuildsPage" */
      '../pages/CompareBuildsPage/CompareBuildsPage.container'
    ),
)
const GuidesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "GuidesPage" */
      '../pages/GuidesPage/GuidesPage'
    ),
)
const ChangePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ChangePage" */
      '../pages/ChangePage/ChangePage'
    ),
)
const ChangesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ChangesPage" */
      '../pages/ChangesPage/ChangesPage'
    ),
)
const InvestigationsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "InvestigationsPage" */
      '../pages/InvestigationsPage/InvestigationsPage'
    ),
)
type Redirect = Readonly<{
  from: string
  to: string
}>

const redirects: ReadonlyArray<Redirect> = [
  {
    from: Routes.DEPRICATED_FAVORITE_BUILDS,
    to: Routes.FAVORITE_BUILDS,
  },
  {
    from: Routes.DEPRICATED_FAVORITE_PROJECTS,
    to: Routes.FAVORITE_PROJECTS,
  },
  {
    from: '*',
    to: isPipelinesEnabledInExclusiveMode() ? Routes.ALL_PIPELINES : Routes.FAVORITE_PROJECTS,
  },
]

const preloadEndpoint = <D extends AnyQueryDefinition>(
  endpoint: Endpoint<D>,
  arg: QueryArgFrom<D>,
) => store.dispatch(loadEndpoint(endpoint, arg))

const favoriteProjectsRoute: EntryPointRoute<any, any> = {
  path: Routes.FAVORITE_PROJECTS,
  entryPoint: FavoriteProjectsPageEntryPoint,
  loader: ({request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
    const url = new URL(request.url)
    const {onlyFavorites} = queryToObject(url.search)
    return {
      preloadedEndpoints: store.dispatch(preloadFavoriteProjectsPage(onlyFavorites !== 'false')),
    }
  },
}

const routes: ReadonlyArray<EntryPointRoute<any, any>> = [
  favoriteProjectsRoute,
  {
    path: Routes.AGENTS,
    entryPoint: AgentsPagesEntryPoint,
  },
  {
    path: Routes.FAVORITE_AGENT_POOLS,
    entryPoint: AgentsPoolsFavoritePageEntryPoint,
    loader: (): PreloadedEndpointsLoaderData => ({
      preloadedEndpoints: store.dispatch(preloadFavoriteAgentPoolsPage()),
    }),
  },
  {
    path: Routes.AGENTS_OVERVIEW,
    entryPoint: AppAgentOverviewEntryPoint,
    loader: (): PreloadedEndpointsLoaderData => ({
      preloadedEndpoints: store.dispatch(preloadAppAgentOverview()),
    }),
  },
  {
    path: Routes.DISCONNECTED_AGENTS_OVERVIEW,
    entryPoint: AgentsOverviewPageEntryPoint,
    loader: (): PreloadedEndpointsLoaderData => ({
      preloadedEndpoints: store.dispatch(preloadAgentsOverviewPage()),
    }),
  },
  {
    path: Routes.AGENTS_UNAUTHORIZED,
    entryPoint: UnauthorizedAgentsPageEntryPoint,
  },
  {
    path: Routes.AGENT,
    entryPoint: AppAgentEntryPoint,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const agentId = toAgentId(params.agentId ?? 0)
      const url = new URL(request.url)
      const {tab} = queryToObject(url.search)
      return {
        preloadedEndpoints: store.dispatch(preloadAgentPage(agentId, tab)),
      }
    },
  },
  {
    path: Routes.AGENT_POOL,
    entryPoint: AgentPoolPageEntryPoint,
  },
  {
    path: Routes.CLOUD_IMAGE,
    entryPoint: AppAgentTypeEntryPoint,
    loader: ({params}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const agentTypeId = toAgentTypeId(params.agentTypeId ?? '')
      return {
        preloadedEndpoints: store.dispatch(preloadAppAgentType(agentTypeId)),
      }
    },
  },
  {
    path: Routes.PROJECT,
    entryPoint: ProjectPageEntryPoint,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const projectId = toProjectId(params.projectId ?? '')
      const url = new URL(request.url)
      const queryParams = queryToObject(url.search)
      const {projectTab} = queryParams
      const investigationsTreeExpandedQueryParam =
        queryParams[UserProperties.EXPAND_PROJECT_INVESTIGATIONS_TESTS_SECTION]
      const isInvestigationsTreeExpanded =
        investigationsTreeExpandedQueryParam != null
          ? investigationsTreeExpandedQueryParam !== 'false'
          : getUserProperty(
              store.getState(),
              UserProperties.EXPAND_PROJECT_INVESTIGATIONS_TESTS_SECTION,
            ) !== 'false'
      return {
        preloadedEndpoints: store.dispatch(
          preloadProjectPage({projectId, projectTab, isInvestigationsTreeExpanded}),
        ),
      }
    },
  },
  {
    path: Routes.FAVORITE_BUILDS,
    Component: FavoriteBuildsPage,
    loader: ({request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const queryParams = queryToObject(url.search)
      const page = Number(queryParams.page ?? 1)

      return {
        preloadedEndpoints: store.dispatch(
          loadBuildsData({
            isPreload: true,
            locator: getLocator({
              withRunningAndQueued: true,
              baseLocator: STARRED_LOCATOR,
            }),
            withPager: true,
            page,
            requestOptions: {
              withBuildTypeDetails: true,
              withSnapshotDependencies: false,
              withQueuedInfo: true,
              withRunningInfo: true,
              withBuildDetails: true,
              essential: true,
            },
          }),
        ),
      }
    },
  },
  {
    path: Routes.BUILD_TYPE,
    entryPoint: BuildTypePageEntryPoint,
    loader: (args: LoaderFunctionArgs): PreloadedEndpointsLoaderData => ({
      preloadedEndpoints: store.dispatch(preloadBuildTypePage(args)),
    }),
  },
  {
    path: Routes.BUILD,
    entryPoint: BuildPageEntryPoint,
    loader: (args: LoaderFunctionArgs): PreloadedEndpointsLoaderData => ({
      preloadedEndpoints: store.dispatch(preloadBuildPage(args)),
    }),
  },
  {
    path: Routes.BUILD_UNKNOWN_BUILDTYPE,
    Component: BuildUnknownBuildTypePage,
  },
  {
    path: Routes.COMPARE_BUILDS,
    Component: CompareBuildsPage,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const sourceId = toBuildId(params.buildIdSource!)
      const url = new URL(request.url)
      const queryParams = queryToObject(url.search)
      const preloadedEndpoints: LoadedEndpoints = [
        preloadEndpoint(
          restApi.endpoints.getBuildNormalizedAsList,
          getSingleBuildArg(getBuildLocator(sourceId)),
        ),
      ]
      if (queryParams.withBuildId != null) {
        const compareWithId = toBuildId(queryParams.withBuildId)
        preloadedEndpoints.push(
          preloadEndpoint(
            restApi.endpoints.getBuildNormalizedAsList,
            getSingleBuildArg(getBuildLocator(compareWithId)),
          ),
        )
        const {endpoint} = getExtensionEndpoint(store.getState(), 'builds-diff') ?? {}
        if (endpoint != null) {
          preloadedEndpoints.push(
            preloadEndpoint(restApi.endpoints.getCompareBuildsList, {
              endpoint,
              sourceId,
              compareWithId,
            }),
          )
        }
      }
      return {preloadedEndpoints}
    },
  },
  {
    path: Routes.TEST,
    entryPoint: TestHistoryPageEntryPoint,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const {testId} = params
      const url = new URL(request.url)
      const {currentProjectId} = queryToObject(url.search)
      const testLocator = `id:${testId}`
      const muteAndInvestigationLocator = `affectedProjectAndParents:(id:${currentProjectId})`
      return {
        preloadedEndpoints: [
          preloadEndpoint(
            restApi.endpoints.getTest,
            getTestArg(testLocator, muteAndInvestigationLocator),
          ),
        ],
      }
    },
  },
  {
    path: Routes.CHANGE,
    Component: ChangePage,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const queryParams = queryToObject(url.search)
      const changeId = toChangeId(params.changeId ?? '')
      const personal = queryParams?.personal === 'true'
      const locator = `id:${stringifyId(changeId)},personal:${personal}`

      const preloadedEndpoints: LoadedEndpoints = [
        preloadEndpoint(restApi.endpoints.getAllChanges, getChangesArg(locator)),
        preloadEndpoint(
          restApi.endpoints.getTabs,
          getTabsArg(getTabParamsKey({changeId, personal}), true),
        ),
        queryParams.showHierarchy === 'true' &&
          preloadEndpoint(restApi.endpoints.getSidebarProjectsNormalized, sidebarArg),
      ]
      return {preloadedEndpoints}
    },
  },
  {
    path: Routes.CHANGES,
    Component: ChangesPage,
    loader: ({request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const {showHierarchy} = queryToObject(url.search)
      return {
        preloadedEndpoints: [
          showHierarchy === 'true' &&
            preloadEndpoint(restApi.endpoints.getSidebarProjectsNormalized, sidebarArg),
        ],
      }
    },
  },
  {
    path: Routes.GUIDES,
    Component: GuidesPage,
  },
  {
    path: Routes.QUEUE,
    entryPoint: QueuePageEntryPoint,
    loader: ({request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const queryParams = queryToObject(url.search)

      const page = (queryParams.page && parseInt(queryParams.page, 10)) || 1
      store.dispatch(
        submitPager({
          precountedPages: 3,
          pageSize: 50,
          lookupLimit: 10000,
          lookupDelta: 10000,
        }),
      )
      const activeAgentPoolIdString = queryParams.agentPoolId
      const activeAgentPoolId =
        activeAgentPoolIdString != null ? toAgentPoolId(activeAgentPoolIdString) : null
      const onlyMyPersonal = queryParams.onlyMyPersonal === 'true'
      const locator = getQueuePageLocator(activeAgentPoolId, onlyMyPersonal)

      return {
        preloadedEndpoints: store.dispatch(
          loadBuildsData({
            isPreload: true,
            locator,
            withPager: true,
            page,
            requestOptions: {
              customEndpoint: '/buildQueue',
              withBuildTypeDetails: true,
              withSnapshotDependencies: false,
              withQueuedInfo: true,
              withBuildDetails: true,
              essential: true,
            },
            extraRequestOptions: {
              customEndpoint: '/buildQueue',
              withShortBuildDetails: true,
              withPipelineRuns: true,
              withAnchorBuild: true,
              essential: true,
            },
          }),
        ),
      }
    },
  },
  isInvestigationsPageEnabled
    ? {
        path: Routes.INVESTIGATIONS,
        Component: InvestigationsPage,
      }
    : null,
  ...pipelinesPagesRoutes,
  {path: Routes.CREATE_PROJECT, entryPoint: CreateProjectPageEntryPoint},
  {path: Routes.NOTIFICATIONS, entryPoint: NotificationsPageEntryPoint},

  // !!! DON'T FORGET !!!
  // !!! 1) ADD the mapping to web-deployment/webapp/WEB-INF/web.xml AND /react-ui/src/routes/shared-routes.json !!!
  // !!! 2) Restart the server
].filter(notNull)

export const getRoutes = (environment: IEnvironment) =>
  routes.map((route): RouteObject => {
    const {loader, ...rest} = entryPointRoute(route, environment)
    return {
      ErrorBoundary: ErrorPage,
      ...rest,
      loader(arg) {
        const {params, request} = arg
        const url = new URL(request.url)

        const availabilityResponse = getAvailabilityResponse(store.getState(), url.pathname)
        if (availabilityResponse != null && availabilityResponse.statusCode !== HTTPCodesEnum.OK) {
          const {buildId, buildTypeId, projectId, agentId, agentTypeId, agentPoolId} = params
          throw new HTTPError(
            getAvailabilityErrorMessage({
              error: availabilityResponse,
              buildId: buildId != null ? toBuildId(buildId) : null,
              buildTypeId: buildTypeId != null ? toBuildTypeId(buildTypeId) : null,
              projectId: projectId != null ? toProjectId(projectId) : null,
              agentId: agentId != null ? toAgentId(agentId) : null,
              agentTypeId: agentTypeId != null ? toAgentTypeId(agentTypeId) : null,
              agentPoolId: agentPoolId != null ? toAgentPoolId(agentPoolId) : null,
            }) ?? '',
            availabilityResponse.statusCode,
          )
        }

        return typeof loader === 'function' ? (loader(arg) ?? {}) : {}
      },
    }
  })
export const createAppRouter = () =>
  createBrowserRouter([
    entryPointRoute(
      {
        path: Routes.BASE,
        entryPoint: AppEntryPoint,
        loader: ({request}): PreloadedEndpointsLoaderData => {
          const url = new URL(request.url)
          const preloadedEndpoints: LoadedEndpoints = []
          if (isProjectSidebarScreen(url.pathname)) {
            preloadedEndpoints.push(...store.dispatch(preloadProjectsSidebar()))
          } else if (isAgentsScreen(url.pathname)) {
            preloadedEndpoints.push(
              preloadEndpoint(
                restApi.endpoints.getAllAgentPreviewsNormalized,
                getAgentPreviewsArg(),
              ),
              preloadEndpoint(restApi.endpoints.getPoolPermissions, undefined),
            )
          }
          if (getAvailabilityResponse(store.getState(), url.pathname) == null) {
            preloadedEndpoints.push(
              preloadEndpoint(restApi.endpoints.getRouteAvailability, url.pathname),
            )
          }
          return {preloadedEndpoints}
        },
        ErrorBoundary: ErrorPage,
        HydrateFallback: Fragment,
        children: [
          ...redirects.map(({from, to}) => ({
            path: from,
            loader() {
              throw redirect(getHref(to))
            },
            element: null,
          })),
          ...getRoutes(getEnvironment()),
        ],
      },
      getEnvironment(),
    ),
    {
      path: '*',
      loader() {
        throw new HTTPError('Page not found', HTTPCodesEnum.NOT_FOUND)
      },
      ErrorBoundary: ErrorPage,
    },
  ])
export type Router = ReturnType<typeof createBrowserRouter>
